import { create } from 'zustand';

export interface Session {
    id: string;
    name: string;
}

type SessionState = {
    activeSession: Session;

    sessions: Session[];

    defaultSession: Session;

    setActiveSession: (sessionId: string) => void;
    setSessions: (sessions: Session[]) => void;
    addSession: (session: Session) => void;
    updateSession: (sessionId: string, updates: Partial<Session>) => void;
    removeSession: (sessionId: string) => void;
    createSession: (name: string) => Session;
    getSessionById: (sessionId: string) => Session | null;
    getSessionByName: (name: string) => Session | null;
    clearActiveSession: () => void;
    clearSessionStore: () => void;
};

const defaultSession: Session = {
    id: 'default',
    name: 'Primary',
};

export const useSessionStore = create<SessionState>((set, get) => ({
    activeSession: defaultSession,
    sessions: [defaultSession],
    defaultSession,

    setActiveSession: (sessionId: string) => {
        set({ activeSession: get().getSessionById(sessionId) || defaultSession });
    },

    setSessions: (sessions: Session[]) => {
        set({ sessions });
    },

    addSession: (session: Session) => {
        set((state) => ({
            sessions: [...state.sessions, session],
        }));
    },

    updateSession: (sessionId: string, updates: Partial<Session>) => {
        set((state) => ({
            sessions: state.sessions.map((session) =>
                session.id === sessionId ? { ...session, ...updates } : session
            ),
            activeSession: state.activeSession?.id === sessionId
                ? { ...state.activeSession, ...updates }
                : state.activeSession,
        }));
    },

    removeSession: (sessionId: string) => {
        set((state) => ({
            sessions: state.sessions.filter((session) => session.id !== sessionId),
            activeSession: state.activeSession?.id === sessionId ? defaultSession : state.activeSession,
        }));
    },

    createSession: (name: string): Session => {
        const newSession: Session = {
            id: name,
            name,
        };

        get().addSession(newSession);
        get().setActiveSession(name);
        return newSession;
    },

    getSessionById: (sessionId: string): Session | null => {
        const state = get();
        if (sessionId === 'default') {
            return state.defaultSession;
        }
        return state.sessions.find((session) => session.id === sessionId) || null;
    },

    getSessionByName: (name: string): Session | null => {
        const state = get();
        if (name === 'Primary') {
            return state.defaultSession;
        }
        return state.sessions.find((session) => session.name === name) || null;
    },

    clearActiveSession: () => {
        set({ activeSession: defaultSession });
    },

    clearSessionStore: () => {
        set({
            activeSession: defaultSession,
            sessions: [defaultSession],
        });
    },
})); 